import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useParams } from "react-router-dom"
import {
  Search, ChevronDown, ChevronUp, Mic, CheckCircle2, XCircle, Download
} from "lucide-react"

type SortKey = "name" | "email" | "role" | "duration"
type SortDir  = "asc" | "desc"

interface Panelist {
  id: string
  name: string
  email: string
  role: "Speaker" | "Moderator" | "Co-host"
  joinedAt: string
  duration: string
  hasVideo: boolean
  hasAudio: boolean
}

const ROLE_STYLE: Record<Panelist["role"], string> = {
  Speaker:   "bg-primary/10 text-primary",
  Moderator: "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400",
  "Co-host": "bg-slate-100  text-slate-600  dark:bg-slate-700 dark:text-slate-400",
}

export default function PanelistList() {
  const t = useTranslation()
  const navigate   = useNavigate()
  const { id }     = useParams()
  const [search,   setSearch]   = useState("")
  const [sortKey,  setSortKey]  = useState<SortKey>("name")
  const [sortDir,  setSortDir]  = useState<SortDir>("asc")

  const mockPanelists: Panelist[] = [
    { id: "p1", name: "Alice Johnson",  email: "alice@co.com",  role: "Moderator",  joinedAt: "09:55 AM", duration: "95 min", hasVideo: true,  hasAudio: true  },
    { id: "p2", name: "Bob Martinez",   email: "bob@co.com",    role: "Speaker",    joinedAt: "10:00 AM", duration: "90 min", hasVideo: true,  hasAudio: true  },
    { id: "p3", name: "Carol White",    email: "carol@co.com",  role: "Speaker",    joinedAt: "10:02 AM", duration: "88 min", hasVideo: false, hasAudio: true  },
    { id: "p4", name: "David Lee",      email: "david@co.com",  role: "Co-host",    joinedAt: "10:00 AM", duration: "90 min", hasVideo: true,  hasAudio: true  },
    { id: "p5", name: "Eva Kim",        email: "eva@co.com",    role: "Speaker",    joinedAt: "10:15 AM", duration: "75 min", hasVideo: true,  hasAudio: false },
  ]

  const filtered = mockPanelists
    .filter(p =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.email.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey]
      if (av < bv) return sortDir === "asc" ? -1 : 1
      if (av > bv) return sortDir === "asc" ?  1 : -1
      return 0
    })

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => d === "asc" ? "desc" : "asc")
    else { setSortKey(key); setSortDir("asc") }
  }

  const SortIcon = ({ col }: { col: SortKey }) =>
    col !== sortKey
      ? <ChevronUp className="w-3 h-3 opacity-20" />
      : sortDir === "asc"
        ? <ChevronUp className="w-3 h-3 text-primary" />
        : <ChevronDown className="w-3 h-3 text-primary" />

  const cols: { key: SortKey; label: string }[] = [
    { key: "name", label: t("webinar.name") },
    { key: "email", label: t("webinar.email") },
    { key: "role", label: t("webinar.role") },
    { key: "duration", label: t("webinar.duration") },
  ]

  return (
    <div className="flex flex-col h-full overflow-y-auto w-full bg-background p-4 md:p-6 gap-5">

      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <button onClick={() => navigate("/webinar")} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.webinar")}</button>
          <span>/</span>
          <button onClick={() => navigate(`/webinar/${id}/analytics`)} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.analytics")}</button>
          <span>/</span>
          <span className="text-slate-700 dark:text-slate-200 font-semibold">{t("webinar.panelists")}</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* <button
              onClick={() => navigate(`/webinar/${id}/analytics`)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </button> */}
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.panelists")}</h1>
              <p className="text-xs text-slate-400 mt-0.5">{mockPanelists.length} {t("webinar.panelistsInWebinar")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t("webinar.searchPanelists")}
                className="pl-8 pr-3 py-2 text-sm bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-slate-900 dark:text-foreground/80 dark:text-white w-[190px]"
              />
            </div>
            <button className="flex items-center gap-1.5 border border-stroke-100 dark:border-border dark:border-border bg-card dark:bg-card rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:border-primary transition-colors">
              <Download className="w-3.5 h-3.5" /> {t("webinar.export")}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Mic className="w-14 h-14 text-slate-200 dark:text-slate-700" />
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{t("webinar.noPanelistsFound")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stroke-100 dark:border-border dark:border-border bg-slate-50 dark:bg-card/80">
                  {cols.map(({ key, label }) => (
                    <th
                      key={key} onClick={() => handleSort(key)}
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-primary transition-colors select-none"
                    >
                      <span className="flex items-center gap-1">{label} <SortIcon col={key} /></span>
                    </th>
                  ))}
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.joinedAt")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.videoAudio")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} className="border-b border-slate-50 dark:border-border/40 hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {p.name[0]}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{p.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{p.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ${ROLE_STYLE[p.role]}`}>
                        {t("webinar." + p.role.toLowerCase().replace("-", ""))}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{p.duration}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{p.joinedAt}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {p.hasVideo
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          : <XCircle      className="w-4 h-4 text-slate-300 dark:text-slate-600" />}
                        {p.hasAudio
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          : <XCircle      className="w-4 h-4 text-slate-300 dark:text-slate-600" />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
